import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import SvgMapper from '../../features/common/SvgMapper';
import { getStudentMarks, getCategoryPerformance } from '../../../application/services/studentDashboard';

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const ProgressCard = () => {
  const { user } = useSelector((state) => state.login.student);
  const email = user?.email || '';
  const [scoreData, setScoreData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!email || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);
    setCategoryLoading(true);
    Promise.all([
      getStudentMarks(email, 1, 50).then((res) => {
        const data = res?.data || [];
        const arr = Array.isArray(data) ? data : [];
        arr.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        setScoreData(arr);
      }).catch((err) => console.error('Error fetching student marks:', err)),
      getCategoryPerformance(email, 1, 50).then((res) => {
        const data = res?.data || [];
        setCategoryData(Array.isArray(data) ? data : []);
      }).catch((err) => console.error('Error fetching category performance:', err)),
    ]).finally(() => {
      setLoading(false);
      setCategoryLoading(false);
    });
  }, [email]);

  const chartData = {
    labels: scoreData.map((item) => formatDate(item.start_time) || item.interview_name?.slice(0, 12)),
    datasets: [
      {
        label: 'Score %',
        data: scoreData.map((item) => parseFloat(item.percent) || 0),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const item = scoreData[ctx.dataIndex];
            return [`${item?.interview_name || ''}: ${ctx.raw}%`, item?.category ? `Category: ${item.category}` : ''].filter(Boolean);
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, minRotation: 45, maxTicksLimit: 12, font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { stepSize: 25 },
      },
    },
  };

  const categoryChartData = {
    labels: categoryData.map((item) => item.category),
    datasets: [
      {
        label: 'Avg Marks',
        data: categoryData.map((item) => parseFloat(item.avg_marks) || 0),
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1,
        minBarLength: 8,
      },
    ],
  };

  const categoryChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const item = categoryData[ctx.dataIndex];
            return [`Avg: ${ctx.raw}`, item?.interview_count ? `Interviews: ${item.interview_count}` : ''].filter(Boolean);
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, minRotation: 45, font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        suggestedMax: Math.max(...categoryData.map((i) => parseFloat(i.avg_marks) || 0), 10),
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
    },
  };

  return (
    <div className='bg-white rounded-lg border border-gray-200 p-6'>
      <h3 className='text-lg font-semibold text-gray-900 mb-6'>Your Progress</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Score Trends</h3>
          <div className="h-64">
            {loading ? (
              <div className="flex items-center justify-center h-64 text-gray-500">
                Loading...
              </div>
            ) : scoreData.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  {SvgMapper.trendingUp?.("w-16 h-16 mx-auto mb-4 opacity-50")}
                  <p>Score progression chart</p>
                  <p className='text-sm'>No data yet. Complete interviews to see your score trends.</p>
                </div>
              </div>
            ) : (
              <Line data={chartData} options={chartOptions} />
            )}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Category Performance</h3>
          <div className="h-64">
            {categoryLoading ? (
              <div className="flex items-center justify-center h-64 text-gray-500">
                Loading...
              </div>
            ) : categoryData.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  {SvgMapper.barChart3?.("w-16 h-16 mx-auto mb-4 opacity-50")}
                  <p>Performance by category</p>
                  <p className='text-sm'>No data yet. Complete interviews to see category performance.</p>
                </div>
              </div>
            ) : (
              <Bar data={categoryChartData} options={categoryChartOptions} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressCard;
